import { createSlice } from "@reduxjs/toolkit";
import { cloneDeep, isEmpty } from "lodash-es";
import { mergedToUniqueById, updateObject } from "utils/helper";

const initialTyping = {
  agent_id: 0,
  name: "",
  conversation_id: 0,
};

const initialState = {
  messages: [],
  conversation: {},
  lastMessageId: 0,
  lastStatusLogId: 0,
  typing: initialTyping,
  agent_online: 0,
  conversation_type: null,
  isFetchingConversation: false,
  chataiResponse: [],
  // Conversation yang "dipromosikan" ke my_inbox secara optimistic (mis. setelah
  // ambil alih via kirim pesan / create chat). Ditahan DI LUAR query cache lalu
  // di-merge saat render list, supaya refetch my_inbox yang masih lag di BE tidak
  // bisa menghapusnya. Auto-dibersihkan begitu conversation asli muncul di cache.
  pinnedConversations: [],
  // Status koneksi Pusher saat ini. Dipakai guard countdown auto-transfer agar
  // tidak false-fire (melempar chat ke agent lain) saat koneksi sedang putus —
  // pada kondisi itu kita mungkin melewatkan balasan customer. Default true
  // supaya perilaku normal tidak terblokir sebelum event koneksi pertama.
  pusherConnected: true,
};

const slice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setMessages: (state, { payload }) =>
      updateObject(state, { messages: payload }),
    appendToMessages: (state, { payload }) =>
      updateObject(state, {
        messages: [...state.messages, payload],
      }),
    setConversation: (state, { payload }) =>
      updateObject(state, {
        conversation: payload,
      }),
    clearMessages: (state) => updateObject(state, { messages: [] }),
    appendBatchMessages: (state, { payload }) => {
      // Untuk memastikan bahwa pesan yang dimasukkan adalah percakapan yang aktif
      const currentConversationId = state.conversation?.id;
      const filteredPayload = payload.filter(
        (row) => row.conversation_id === currentConversationId
      );
      if (isEmpty(filteredPayload)) {
        return state;
      }

      // Menambahkan elemen baru hanya jika id belum ada di dalam existingMessages
      const messages = mergedToUniqueById(state.messages, filteredPayload);

      return updateObject(state, { messages });
    },
    // Merge pesan ke redux TANPA filter conversation_id (union by id). Dipakai
    // untuk re-sync panel chat dari cache (mengisi history conversation yang baru
    // dilengkapi di cache). Aman terhadap pesan optimistic karena union by id.
    mergeMessages: (state, { payload }) => {
      if (isEmpty(payload)) return state;
      return updateObject(state, {
        messages: mergedToUniqueById(state.messages, payload),
      });
    },
    updateMessage: (state, { payload }) => {
      const newState = [...state.messages];
      const findIndex = newState.findIndex((row) => row.id === payload.id);
      if (newState[findIndex]) {
        const currentRow = newState[findIndex];
        newState[findIndex] = { ...currentRow, ...payload.data };
      }
      return updateObject(state, { messages: newState });
    },
    setChat: (state, { payload }) => updateObject(state, payload),
    resetChat: (state) => updateObject(state, initialState),
    setLastMessageId: (state, { payload }) =>
      updateObject(state, { lastMessageId: payload }),
    updateStatusMessages: (state, { payload }) => {
      const updatedMessages = cloneDeep(state.messages);
      payload.forEach((item) => {
        // Mencari indeks pesan berdasarkan message_id
        const messageIndex = updatedMessages.findIndex(
          (msg) => msg.id === item.message_id
        );

        // Melakukan pembaruan status jika pesan ditemukan
        if (messageIndex !== -1) {
          updatedMessages[messageIndex].status_id = item.status_id;
          updatedMessages[messageIndex].error_code_id = item.error_code_id;
        }
      });

      return updateObject(state, { messages: updatedMessages });
    },
    setLastStatusLogId: (state, { payload }) =>
      updateObject(state, { lastStatusLogId: payload }),
    updateConversationContact: (state, { payload: { id, ...payload } }) => {
      const updateConversation = cloneDeep(state.conversation);
      if (!isEmpty(updateConversation?.contact)) {
        const contact = updateConversation?.contact || {};
        if (contact.id === id) {
          updateConversation.contact = {
            ...updateConversation.contact,
            ...payload,
          };
        }
      }

      return updateObject(state, {
        conversation: updateConversation,
      });
    },
    setTyping: (state, { payload }) => updateObject(state, { typing: payload }),
    resetTyping: (state) => updateObject(state, { typing: initialTyping }),
    setOnlineStatus: (state, { payload }) => {
      return updateObject(state, { agent_online: payload });
    },
    setConversationType: (state, { payload }) => {
      return updateObject(state, { conversation_type: payload });
    },
    setCurrentAgentId: (state, { payload }) => {
      return updateObject(state, {
        conversation: {
          ...cloneDeep(state.conversation),
          current_agent_id: payload,
        },
      });
    },
    setIsFetchingConversation: (state, { payload }) => {
      return updateObject(state, { isFetchingConversation: payload });
    },
    setPusherConnected: (state, { payload }) => {
      return updateObject(state, { pusherConnected: payload });
    },
    // Pin satu conversation (dedupe by id). payload = objek conversation utuh
    // (minimal id, phone_number_id, contact, messages[]) agar bisa langsung
    // dirender di list my_inbox.
    pinConversation: (state, { payload }) => {
      if (isEmpty(payload) || !payload.id) return state;
      const exists = state.pinnedConversations.some(
        (row) => row.id === payload.id
      );
      if (exists) {
        return updateObject(state, {
          pinnedConversations: state.pinnedConversations.map((row) =>
            row.id === payload.id ? { ...row, ...payload } : row
          ),
        });
      }
      return updateObject(state, {
        pinnedConversations: [...state.pinnedConversations, payload],
      });
    },
    unpinConversation: (state, { payload }) => {
      return updateObject(state, {
        pinnedConversations: state.pinnedConversations.filter(
          (row) => row.id !== payload
        ),
      });
    },
    setChatAIResponse: (state, { payload }) => {
      const newItem = {
        open: true,
        message: payload.text,
        question: payload.question ?? "",
        phoneNumberId: payload.phone_number_id ?? 0,
        messageId: payload.message_id ?? 0,
        conversationId: payload.conversation_id ?? 0,
      };
      return updateObject(state, {
        chataiResponse: [...state.chataiResponse, newItem],
      });
    },
    resetChatAIResponse: (state) => {
      return updateObject(state, { chataiResponse: [] });
    },
    removeChatAIResponse: (state, { payload }) => {
      const { conversationId, messageId } = payload;
      const chataiResponse = state.chataiResponse.filter(
        (item) =>
          !(item.conversationId === conversationId && item.messageId === messageId)
      );
      return updateObject(state, { chataiResponse });
    },
    removeChatAIResponseByConversation: (state, { payload }) => {
      const { conversationId } = payload;
      const chataiResponse = state.chataiResponse.filter(
        (item) => item.conversationId !== conversationId
      );
      return updateObject(state, { chataiResponse });
    },
  },
});

export const {
  setMessages,
  appendToMessages,
  setConversation,
  updateMessage,
  setChat,
  setLastMessageId,
  updateStatusMessages,
  setLastStatusLogId,
  appendBatchMessages,
  mergeMessages,
  updateConversationContact,
  clearMessages,
  resetChat,
  setTyping,
  resetTyping,
  setOnlineStatus,
  setConversationType,
  setCurrentAgentId,
  setIsFetchingConversation,
  setPusherConnected,
  pinConversation,
  unpinConversation,
  setChatAIResponse,
  resetChatAIResponse,
  removeChatAIResponse,
  removeChatAIResponseByConversation,
} = slice.actions;

export default slice;
